import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import getIdToken from '../components/getIdToken';
import { initializeRemoteConfig, getPlansEnabled, getCustomersEnabled } from '../services/remoteConfig';

const CreditsContext = createContext();

export const useCredits = () => {
    const context = useContext(CreditsContext);
    if (!context) {
        throw new Error('useCredits must be used within a CreditsProvider');
    }
    return context;
};

export const CreditsProvider = ({ children }) => {
    const [planData, setPlanData] = useState({
        subscription: null,
        remaining_credit: 0,
        monitors: 0,
        domainMonitorsUnlimited: false
    });
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    // `loading` flips to false as soon as auth resolves, but the credit balance is
    // fetched asynchronously AFTER that — so consumers that render the balance need
    // a separate "has the plan/credit fetch finished at least once" signal to avoid
    // flashing a premature 0. Stays true after the first load (refreshes update the
    // number in place, no skeleton blink).
    const [planLoaded, setPlanLoaded] = useState(false);
    /*
     * The balance could not be read. Named creditsError, NOT error: Dashboard
     * already destructures an `error` from here and would replace the whole app
     * with it. Without this a failed fetch left remaining_credit at its initial
     * 0 and set planLoaded — a user with credits was shown 0, and might buy more.
     *
     * True only while NO balance has been read since sign-in: remaining_credit
     * is then just the initial 0, not a number. After one good read, a refresh
     * that fails leaves that real figure standing rather than blanking it.
     */
    const [creditsError, setCreditsError] = useState(false);
    // The uid whose balance planData holds (null = none read yet). A uid, not a
    // boolean: auth can go from user A straight to user B with no null between
    // (custom-token sign-in), and B must not inherit A's "read" — or A's number.
    const balanceUidRef = useRef(null);
    // Master switch (Remote Config). Default FALSE → credits-only, because plans
    // are retired and the page must not appear while config is still loading. See
    // remoteConfig.js for why every fallback points the same way now.
    const [plansEnabled, setPlansEnabled] = useState(false);
    // Customers/Reservations dashboard (mock data) — default OFF until enabled.
    const [customersEnabled, setCustomersEnabled] = useState(false);
    // Until Remote Config answers, the `false` above means "not known yet", not
    // "off" — a route guard has to wait for this rather than redirect.
    const [flagsLoaded, setFlagsLoaded] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                await initializeRemoteConfig();
                if (active) { setPlansEnabled(getPlansEnabled()); setCustomersEnabled(getCustomersEnabled()); }
            } catch (error) {
                if (active) { setPlansEnabled(false); setCustomersEnabled(false); } // fail safe → hidden
            } finally {
                if (active) setFlagsLoaded(true);
            }
        })();
        return () => { active = false; };
    }, []);

    const fetchUserPlanData = async (currentUser) => {
        if (!currentUser) return;

        try {
            const idToken = await getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}xeo/v1/user/${currentUser.uid}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${idToken}`
                }
            });

            if (!response.ok) throw new Error('Failed to fetch user data');

            const data = await response.json();

            // Guard against a 200 with an unexpected/empty body (no `user`): without
            // this `data.user.activeSubscription` throws and the whole credits context
            // silently zeros out for the app.
            const u = data?.user || {};
            balanceUidRef.current = currentUser.uid;
            setCreditsError(false);
            setPlanData({
                subscription: u.activeSubscription || null,
                remaining_credit: u.totalActiveCredits || 0,
                monitors: u.activeSubscription?.features?.monitors_allowed || 0,
                // Backend (manager 4.28.17) sends this top-level boolean and OMITS
                // monitors_allowed when true. Read it first; consumers treat true
                // as unlimited and never fall back to the (absent) monitors_allowed.
                domainMonitorsUnlimited: u.domain_monitors_unlimited === true,
                planName: u.activeSubscription?.PlanName
            });
            // Returned so a caller waiting on a top-up can tell when it lands.
            return u.totalActiveCredits || 0;
        } catch (error) {
            console.error('Error fetching user plan data:', error);
            setCreditsError(balanceUidRef.current !== currentUser.uid);
            return null;
        } finally {
            setPlanLoaded(true);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);

            if (currentUser) {
                // A different account than the balance on screen: drop that
                // balance first, so nothing shows A's credits as B's.
                if (balanceUidRef.current && balanceUidRef.current !== currentUser.uid) {
                    balanceUidRef.current = null;
                    setPlanLoaded(false);
                    setPlanData({
                        subscription: null,
                        remaining_credit: 0,
                        monitors: 0,
                        domainMonitorsUnlimited: false
                    });
                }
                fetchUserPlanData(currentUser);
            } else {
                // Reset to initial state when user logs out
                balanceUidRef.current = null;
                setCreditsError(false);
                setPlanData({
                    subscription: null,
                    remaining_credit: 0,
                    monitors: 0,
                    domainMonitorsUnlimited: false
                });
                setPlanLoaded(true); // nothing to load when signed out — don't hang a skeleton
            }
        });

        return () => unsubscribe();
    }, []);

    /*
     * RE-READ WHEN THE TAB COMES BACK. The balance was read once per sign-in,
     * so a purchase or a hosting upgrade in one tab left every other open tab
     * showing the old number until a reload. Throttled to once a minute, so
     * flicking between tabs doesn't hammer the endpoint.
     */
    useEffect(() => {
        if (!user) return undefined;
        let lastAt = Date.now();
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            // Unthrottled while there is no balance at all: that wait was a minute
            // of "you only have 0 credits" for a read that had merely failed.
            if (balanceUidRef.current === user.uid && Date.now() - lastAt < 60000) return;
            lastAt = Date.now();
            fetchUserPlanData(user).catch(() => { /* creditsError carries it */ });
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    /** @returns the fresh balance, or null when it could not be read. */
    const refreshCredits = async () => {
        if (user) {
            try {
                return await fetchUserPlanData(user);
            } catch (error) {
                console.error('Failed to refresh credits:', error);
            }
        }
        return null;
    };

    return (
        <CreditsContext.Provider value={{
            planData,
            refreshCredits,
            loading,
            planLoaded,
            creditsError,
            user,
            plansEnabled,
            customersEnabled,
            flagsLoaded
        }}>
            {children}
        </CreditsContext.Provider>
    );
};